import React from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Music } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import { PreviewProps, getFollowersCount, formatFollowersCount } from './types';

export const InstagramPreview: React.FC<PreviewProps> = ({ content, brand, mediaUrl, mediaUrls, format = 'regular', engagement, hideEngagementCounts }) => {
  const { t } = useTranslation();
  
  // Récupérer le nombre de followers Instagram pour des valeurs par défaut plus réalistes
  const instagramFollowers = getFollowersCount(brand, 'instagram');
  
  // Calculer des valeurs par défaut basées sur les followers (engagement typique ~3-5%)
  const defaultLikes = instagramFollowers ? Math.floor(instagramFollowers * 0.04) : 3241;
  const defaultComments = instagramFollowers ? Math.floor(instagramFollowers * 0.01) : 42;
  const defaultShares = instagramFollowers ? Math.floor(instagramFollowers * 0.005) : 15;

  // Regular Instagram post
  if (format === 'regular') {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden max-w-sm mx-auto">
        {/* Header */}
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden">
                {brand?.logo ? (
                  <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <span className="font-semibold text-sm">
                {brand?.name || 'company_name'}
              </span>
            </div>
            <button className="p-1">
              <MoreHorizontal className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Media (primary element for Instagram) */}
        <div className="relative w-full pb-[125%] bg-gray-100 flex items-center justify-center">
          <MediaDisplay
            mediaUrl={(mediaUrls && mediaUrls.length > 0 ? mediaUrls[0] : mediaUrl) as string}
            altText="Instagram post"
            type="image"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        {/* Pagination dots for multi-images */}
        {mediaUrls && mediaUrls.length > 1 && (
          <div className="px-3 mt-2 flex items-center justify-center gap-1.5">
            {(() => {
              const total = Math.min(mediaUrls.length, 10);
              const grayCount = total - 1;
              const maxSize = 8; // px for the first dot
              const minSize = 4; // px for the last gray dot
              return Array.from({ length: total }).map((_, idx) => {
                const isActive = idx === 0;
                let size = maxSize;
                if (!isActive && grayCount > 0) {
                  const grayIdx = idx - 1; // 0..grayCount-1
                  const t = grayIdx / (grayCount - 1 || 1); // normalized
                  size = Math.round(maxSize + (minSize - maxSize) * t); // linear shrink from 8px -> 4px
                }
                return (
                  <span
                    key={idx}
                    style={{ width: size, height: size }}
                    className={`inline-block rounded-full ${isActive ? 'bg-[#2563eb]' : 'bg-gray-300'} opacity-90`}
                  />
                );
              });
            })()}
          </div>
        )}

        {/* Actions */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button aria-label={t('previews.actions.like', 'Like')}>
                <Heart className="w-6 h-6" />
              </button>
              <button aria-label={t('previews.actions.comment', 'Comment')}>
                <MessageCircle className="w-6 h-6" />
              </button>
              <button aria-label={t('previews.actions.send', 'Send')}>
                <Send className="w-6 h-6" />
              </button>
            </div>
            <button>
              <Bookmark className="w-6 h-6" />
            </button>
          </div>

          {/* Likes */}
          {!hideEngagementCounts && (
            <div className="mb-1">
              <p className="text-sm font-semibold">
                {engagement?.likes
                  ? `${engagement.likes.toLocaleString()} ${t('previews.actions.like', 'Like').toLowerCase()}${engagement.likes === 1 ? '' : 's'}`
                  : `${defaultLikes.toLocaleString()} ${t('previews.actions.like', 'Like').toLowerCase()}s`}
              </p>
            </div>
          )}

          {/* Caption */}
          <div className="mb-1">
            <p className="whitespace-pre-line" style={{ fontSize: '0.8rem' }}>
              <span className="font-semibold mr-1">{brand?.name || 'company_name'}</span>
              {content}
            </p>
          </div>

          {/* Comments preview */}
          {!hideEngagementCounts && (
            <p className="text-sm text-gray-500">
              {engagement?.comments
                ? t('previews.viewAllComments', 'View all {{count}} comments', { count: engagement.comments })
                : t('previews.viewAllComments', 'View all {{count}} comments', { count: defaultComments })}
            </p>
          )}

          {/* Time */}
          <p className="text-xs text-gray-400 mt-1">{t('previews.time.hoursAgo', '{{count}} HOURS AGO', { count: 2 })}</p>
        </div>
      </div>
    );
  }
  
  // Instagram Reels preview
  return (
    <div className="bg-black text-white rounded-xl shadow-md border border-gray-800 overflow-hidden max-w-[320px] mx-auto">
      <div className="relative w-full aspect-[9/16] bg-gray-900 flex flex-col">
        {/* Main content */}
        <div className="flex-1 relative">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="Instagram Reel"
            type="video"
            fullSize={false}
            className="h-full w-full object-cover"
            controls={false}
            autoPlayOnMount
            loop
            muted
            aspectRatio="9/16"
          />
          
          {/* Right side actions */}
          <div className="absolute right-3 bottom-24 flex flex-col items-center space-y-5">
            <button className="flex flex-col items-center">
              <Heart className="w-7 h-7" />
              <span className="text-xs mt-1">
                {engagement?.likes ? (engagement.likes >= 1000 ? `${(engagement.likes / 1000).toFixed(1)}k` : engagement.likes.toString()) : '142k'}
              </span>
            </button>
            <button className="flex flex-col items-center">
              <MessageCircle className="w-7 h-7" />
              <span className="text-xs mt-1">
                {engagement?.comments ? (engagement.comments >= 1000 ? `${(engagement.comments / 1000).toFixed(1)}k` : engagement.comments.toString()) : '1,024'}
              </span>
            </button>
            <button className="flex flex-col items-center">
              <Send className="w-7 h-7" />
              <span className="text-xs mt-1">
                {engagement?.shares ? (engagement.shares >= 1000 ? `${(engagement.shares / 1000).toFixed(1)}k` : engagement.shares.toString()) : '483'}
              </span>
            </button>
            <button className="flex flex-col items-center">
              <Bookmark className="w-7 h-7" />
            </button>
            <button className="flex flex-col items-center">
              <MoreHorizontal className="w-7 h-7" />
            </button>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                {brand?.logo ? (
                  <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <span className="font-semibold text-sm">{brand?.name || 'company_name'}</span>
              <button className="border border-white text-white text-xs px-2 py-1 rounded">
                {t('previews.follow', 'Follow')}
              </button>
            </div>
            <p className="mb-3 whitespace-pre-line" style={{ fontSize: '0.8rem' }}>{content}</p>
            <div className="flex items-center">
              <Music className="w-3 h-3 mr-2" />
              <div className="flex-1 text-xs truncate">
                {t('previews.originalAudio', 'Original audio')} • {brand?.name || 'company_name'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};